/** The line popover (moved from `quote/quote-editors.tsx` at step 8). */
import { useEffect, useState } from 'react'
import {
  CUSTOMER_NAME_MAX, QUOTE_LINE_KIND_LABELS, customerNameToStore,
  type LineHandling, type QuoteLineView, type QuoteServiceView,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Band, Button } from '../../components/primitives.js'
import { FieldShell } from '../../components/fields.js'
import {
  EditorActions, EditorHeader, EditorInput, EditorShell, FooterTotal, FOOTER_BUTTON,
} from '../editor-shell.js'
import { formatNumber, formatRate, formatUnitCost } from '../../lib/format.js'
import { OptionsBlock } from '../fields/options-block.js'
import {
  AT_MEASURE, CUSTOMER_NAME_LABEL, CUSTOMER_NAME_QUOTE_HINT, CUSTOMER_NAME_RECIPE_HINT,
  LINE_TOTAL, MARGIN_TITLE, MARKUP, OUR_COST_PER, QTY_BASE_LABEL, QUOTED_PER, RESET,
  STANDARD_PER,
} from '../copy.js'
import type { TileKind } from '../kind-tile.js'
import { IconLock } from '../../frame/icons.js'
import { QuantityRuleEditor } from '../quantity/quantity-rule-editor.js'
import { UnitInput } from '../quantity/unit-input.js'
import type { DraftTier } from '../quantity/tiers.js'
import { LegacyBands, type LegacyBand } from '../quantity/legacy-bands.js'
import { BIG_BOX, BIG_INPUT } from './big-field.js'

// ---------------------------------------------------------------------------
// §5.2 — the line editor
// ---------------------------------------------------------------------------


/**
 * Markup to one decimal, without a trailing nought: cb-19 draws `68.6%` and
 * cb-07 draws `35%` for 35.006, and one rule says both.
 */
const markupWords = (pct: number): string => `${Number(pct.toFixed(1))}%`

/**
 * One line, opened from its name — `design-remediation.md` WP5, drawn from the
 * middle popup of `21-catalog-packages/html/packages-popups.html`.
 *
 * The as-built pair showed a bare unit label floating under the title, no way
 * to see what the line costs beside what it sells for, and "Handling: Consume"
 * in the header with nowhere to live. The mockup's answer is a header that
 * says what this line *is* — tile, name, kind · division · cost basis — a
 * quantity you can see from across the desk, three price cells side by side,
 * and one sentence of arithmetic under them.
 *
 * Handling is a checkbox in the group now, and only for materials: "Consumes
 * stock" is what the field means, and a dropdown of two answers in the header
 * was giving a footnote the most valuable space in the box.
 */
export function LinePopover({
  line, service, anchor, onClose, onSave, onReset, onRemove, problem,
}: {
  line: QuoteLineView
  service: QuoteServiceView
  anchor: HTMLElement | null
  onClose: () => void
  onSave: (patch: {
    qty?: number; rate?: number; editReason?: string
    handling?: LineHandling; optional?: boolean; taxable?: boolean; showOnProposal?: boolean
    /** The name the customer reads on this quote; null goes back to the description. */
    customerName?: string | null
  }) => Promise<boolean>
  onReset: () => void
  /**
   * Take this line off the quote.
   *
   * On the row's ⋮ as well, and here because the box you opened to look at a
   * line is where you decide you do not want it — the mockup puts Remove in
   * the footer beside Done for exactly that reason.
   */
  onRemove: () => void
  /**
   * What went wrong, if the last save did.
   *
   * The page holds the error and draws it in a banner — which is behind this
   * popup, so clicking Done on an approved quote looked like clicking Done and
   * nothing happening. The box that asked for the change says why it was
   * refused.
   */
  problem?: string | null
}) {
  const [qty, setQty] = useState(String(line.qty))
  const [rate, setRate] = useState(line.rate.toFixed(2))
  const [reason, setReason] = useState(line.editReason ?? '')
  const [handling, setHandling] = useState<LineHandling>(line.handling ?? 'consume')
  const [optional, setOptional] = useState(line.optional)
  const [taxable, setTaxable] = useState(line.taxable)
  const [onProposal, setOnProposal] = useState(line.showOnProposal)
  /* Empty is "the same as the name" — the description is the box's placeholder. */
  const [customerName, setCustomerName] = useState(line.customerName ?? '')
  const [busy, setBusy] = useState(false)

  const quoted = Number(rate)
  const below = Number.isFinite(quoted) && quoted < line.standardRate
  const shortfall = line.standardRate - quoted
  const blocked = below && reason.trim().length === 0

  /*
   * The margin on this one line, so "should I?" is answerable without leaving
   * the box. Cost per unit is the line's cost over its quantity — the only
   * number here the server did not send, and the only one that cannot be
   * wrong, because both halves came from the same row.
   */
  const unitCost = line.costEach ?? (line.qty > 0 ? line.cost / line.qty : 0)
  /*
   * The server's margin while the price is the one it priced; only a price
   * being typed is worked out here, as a preview of what Done will send
   * (folder 44 §2 rule 1). One decimal, as the line's own Margin cell says it.
   */
  const lineMargin = quoted === line.rate && line.marginPct != null
    ? line.marginPct
    : quoted > 0 ? Math.round(((quoted - unitCost) / quoted) * 1000) / 10 : null
  const markup = unitCost > 0 ? ((quoted - unitCost) / unitCost) * 100 : null
  const total = Number.isFinite(quoted) && Number.isFinite(Number(qty))
    ? Number(qty) * quoted
    : line.amount

  async function done() {
    setBusy(true)
    try {
      const patch: Parameters<typeof onSave>[0] = {}
      // Zero is a quantity — a line kept on the quote and priced at nothing.
      const typed = qty.trim() === '' ? Number.NaN : Number(qty)
      if (Number.isFinite(typed) && typed >= 0 && typed !== line.qty) patch.qty = typed
      if (quoted !== line.rate) { patch.rate = quoted; patch.editReason = reason.trim() }
      /*
       * The same fallback on both sides of the comparison.
       *
       * The state starts at `line.handling ?? 'consume'`, so on a line whose
       * handling was never set this read "'consume' !== null" and sent a
       * handling change with every save — an audit row saying somebody chose
       * what they had not touched.
       */
      if (line.kind === 'item' && handling !== (line.handling ?? 'consume')) {
        patch.handling = handling
      }
      if (optional !== line.optional) patch.optional = optional
      if (taxable !== line.taxable) patch.taxable = taxable
      if (onProposal !== line.showOnProposal) patch.showOnProposal = onProposal
      /*
       * The server's own rule, run here first so a Done that changed nothing
       * sends nothing: blank, or the description typed out, is null.
       */
      const named = customerNameToStore(customerName, line.description)
      if (named !== (line.customerName ?? null)) patch.customerName = named
      if (await onSave(patch)) onClose()
    } finally {
      setBusy(false)
    }
  }

  /** "from 640 sq ft ÷ 80 · edit to override" — where a service's quantity came from. */
  const provenance = service.serviceId && service.measure && !line.handEdited
    ? `from ${formatNumber(service.measure)} ${service.measureUnit} · edit to override`
    : undefined

  /* "Material · 1.2 bag × $55.11" — what this line is (Board 7). */
  const subtitle = [
    QUOTE_LINE_KIND_LABELS[line.kind],
    `${formatNumber(line.qty)} ${line.unit} × ${formatRate(line.rate)}`,
  ].join(' · ')

  return (
    <EditorShell
      open
      onClose={onClose}
      anchor={anchor}
      label={line.description}
      header={<EditorHeader kind={line.kind} name={line.description} facts={subtitle} />}
      footer={(
        /*
         * Board 7's one footer — total · Remove · Done — and the line is the
         * one popover that also carries Reset, left of Remove.
         */
        <EditorActions
          fact={<FooterTotal label={LINE_TOTAL} amount={formatRate(total)} />}
          extra={(
            <Button className={FOOTER_BUTTON} onClick={() => { onReset(); onClose() }}>
              {RESET}
            </Button>
          )}
          onRemove={() => { onRemove(); onClose() }}
          onDone={() => void done()}
          busy={busy}
          disabled={blocked}
        />
      )}
    >
      <div className="flex flex-col gap-3.5">
        {problem && <Band tone="amber">{problem}</Band>}

        {/*
          * What the customer reads for this line on this quote (Aaron, step
          * 24). At the top because it is the line's name, but the quantity
          * below keeps the focus: renaming is the rarer errand. The header
          * goes on saying the catalog name, which is the office's word.
          */}
        <FieldShell label={CUSTOMER_NAME_LABEL} hint={CUSTOMER_NAME_QUOTE_HINT}>
          <EditorInput
            value={customerName}
            placeholder={line.description}
            maxLength={CUSTOMER_NAME_MAX}
            aria-label={CUSTOMER_NAME_LABEL}
            onChange={(event) => setCustomerName(event.target.value)}
          />
        </FieldShell>

        {/*
          * The quantity, big. It is what the box is opened for nine times in
          * ten, and the mockup gives it a 40px field with the unit inside so
          * the number and what it counts are one thing rather than two.
          */}
        <FieldShell label="Quantity" hint={provenance}>
          {/*
            * One box, not two. The unit lives inside the field, so the border
            * and the focus ring belong to the wrapper and the input inside it
            * has neither — an input with its own border drew a second rounded
            * rectangle inside the first.
            */}
          <span data-editor-input="" data-size="big" className={cn(BIG_BOX)}>
            <input
              autoFocus
              value={qty}
              inputMode="decimal"
              aria-label="Quantity"
              onChange={(event) => setQty(event.target.value)}
              className={BIG_INPUT}
            />
            <span className="shrink-0 text-[12px] font-medium text-text-faint">{line.unit}</span>
          </span>
          {/*
            * How this quantity is bought, locked — the sentence the row shows
            * under the line (D5: one line under Quantity in the popover and
            * the phone sheet), at the quantity the server last priced.
            */}
          {line.buying?.text && (
            <span data-buying-hint="" className={cn('mt-1 flex items-start gap-1.5 text-[11.5px] leading-[1.4]',
              line.buying.tone === 'bad' ? 'text-danger'
                : line.buying.tone === 'warn' ? 'text-warn' : 'text-text-muted')}>
              <IconLock size={11} className="mt-px shrink-0" />
              {line.buying.text}
            </span>
          )}
        </FieldShell>

        {/* Cost, standard, quoted — side by side, because the question is
            always "how do these three compare". */}
        <div className="grid grid-cols-3 gap-3">
          <FieldShell label={`Our cost / ${line.unit}`}>
            <span className={cn(readOnlyCell)}>{formatUnitCost(unitCost)}</span>
          </FieldShell>
          <FieldShell label={`Standard / ${line.unit}`}>
            <span className={cn(readOnlyCell)}>{formatRate(line.standardRate)}</span>
          </FieldShell>
          <FieldShell label={`Quoted / ${line.unit}`}>
            <input
              value={rate}
              inputMode="decimal"
              aria-label="Quoted price"
              onChange={(event) => setRate(event.target.value)}
              className={cn('tnum h-9 w-full rounded-field border bg-surface px-3 text-right',
                'text-[14px] font-semibold text-text focus:outline-none focus:ring-2',
                below
                  ? 'border-warn focus:ring-warn-bg'
                  : 'border-line focus:border-accent-line focus:ring-accent-ring')}
            />
          </FieldShell>
        </div>

        {/* cb-07: Line total · Margin · Markup, the recipe popover's readout. */}
        <div className="grid grid-cols-[auto_auto_auto_1fr] gap-x-8 rounded-[10px] bg-surface-alt px-4 py-3">
          <span className="flex flex-col text-[11.5px] text-text-muted">
            {LINE_TOTAL}
            <b className="tnum text-[20px] font-bold text-text">{formatRate(total)}</b>
          </span>
          <span className="flex flex-col text-[11.5px] text-text-muted">
            {MARGIN_TITLE}
            <b className={cn('tnum text-[20px] font-bold',
              lineMargin === null || lineMargin >= 0 ? 'text-accent' : 'text-danger')}>
              {lineMargin === null ? '—' : `${lineMargin.toFixed(1)}%`}
            </b>
          </span>
          <span className="flex flex-col text-[11.5px] text-text-muted">
            {MARKUP}
            <b className="tnum text-[20px] font-bold text-text">
              {markup === null ? '—' : markupWords(markup)}
            </b>
          </span>
        </div>

        {/*
          * The gate. Not a nag: this number ends up in a margin report and in
          * somebody's commission, and "why is this patio at 21%" is a question
          * the audit row can answer six months later and nobody's memory can.
          * Above standard needs nothing — charging more explains itself.
          */}
        {below && (
          <div className="flex flex-col gap-2 rounded-field bg-warn-bg p-2.5">
            <p className="m-0 text-[12px] leading-[1.4] text-warn">
              Below standard by <b>{formatRate(shortfall)}</b> / {line.unit}.
              Reason (shows in the audit, not to the customer):
            </p>
            <input
              value={reason}
              placeholder="price match…"
              aria-label="Reason for the price"
              onChange={(event) => setReason(event.target.value)}
              className={cn('h-8 w-full rounded-field border border-warn bg-surface px-2',
                'text-body text-text')}
            />
          </div>
        )}

        <OptionsBlock
          level="line"
          optional={{ checked: optional, onChange: setOptional }}
          showOnProposal={{ checked: onProposal, onChange: setOnProposal }}
          taxable={{ checked: taxable, onChange: setTaxable }}
          /*
           * Materials only: "Consumes stock" is what `handling` means, and the
           * alternative — deploy and pick up later — is the unticked state of
           * exactly that sentence.
           */
          consumesStock={line.kind === 'item'
            ? {
              checked: handling === 'consume',
              onChange: (next) => setHandling(next ? 'consume' : 'deploy_return'),
            }
            : undefined}
        />
      </div>
    </EditorShell>
  )
}

/** A price this box shows and does not take — same box, no caret. */
const readOnlyCell = 'tnum flex h-9 w-full items-center justify-end rounded-field border '
  + 'border-line bg-field px-3 text-[14px] font-semibold text-text-body'

// ---------------------------------------------------------------------------
// The same popover on a recipe line (cb-19 cells 2–3, phone cb-22 cells 3–4)
// ---------------------------------------------------------------------------

/** What a recipe line's popover edits — the rule, its tiers and the line's own flags. */
export interface RecipeLineDraft {
  base: string
  rate: string
  tiers: DraftTier[] | null
  optional: boolean
  showOnProposal: boolean
  /** `null` follows the catalog record. */
  taxable: boolean | null
  /** `null` is the standard price. */
  priceOverride: number | null
  /**
   * Consumes stock (0119): `null` follows the kind, which for a material is
   * consume — so only the unticked box, deploy and pick up later, is stored.
   */
  handling: LineHandling | null
  /** Bands somebody typed by hand that are not stacked tiers — kept as they are (D6). */
  legacyBands: LegacyBand[] | null
  /**
   * The line's own customer-facing name (0126); `null` follows the catalog
   * record's, else its name.
   */
  customerName: string | null
}

/** The server's figures for the line at the sample — never worked out here. */
export interface RecipeLineFigures { amount: string; marginPct: number | null; markupPct: number | null }

/**
 * A recipe line, opened from its ⋯ or its tiers chip (builder-engine §6.3).
 *
 * The same box as a quote line's — the header, the three price cells and the
 * Options block are the ones above — with **How much** (base, rate, tiers) in
 * place of the quantity, because on a recipe the quantity is a rule the
 * measure resolves, not a number somebody typed. Its figures are the server
 * preview's (`onPreview`), re-asked as the rule changes.
 */
export function RecipeLinePopover({
  name, kind, facts, qtyUnit, priceUnit, measureUnit, typeName, sample, unitCost, standard,
  draft: initial, catalogTaxable, catalogCustomerName, onPreview, onDone, onRemove, onClose,
  anchor, layer,
}: {
  name: string
  kind: TileKind
  /** "Labor · a line of Mulch beds · opened from its ⋯ or its tiers chip". */
  facts: string
  qtyUnit: string
  priceUnit: string
  /** `null` is No measure: the base is the quantity and nothing scales. */
  measureUnit: string | null
  typeName: string
  sample: number
  unitCost: number
  standard: number
  draft: RecipeLineDraft
  /** What `taxable: null` resolves to — the catalog record's own box. */
  catalogTaxable: boolean
  /**
   * What `customerName: null` resolves to — the catalog record's own
   * customer-facing name; null or absent is the record's name (`name`).
   */
  catalogCustomerName?: string | null
  onPreview: (draft: RecipeLineDraft) => Promise<RecipeLineFigures | null>
  onDone: (draft: RecipeLineDraft) => void
  onRemove: () => void
  onClose: () => void
  anchor: HTMLElement | null
  /** Over the service pop-out, a layer above it. */
  layer?: number
}) {
  const [draft, setDraft] = useState(initial)
  const [quoted, setQuoted] = useState((initial.priceOverride ?? standard).toFixed(2))
  /* Kept apart from the draft: the figures do not depend on it, so typing re-asks nothing. */
  const [customerName, setCustomerName] = useState(initial.customerName ?? '')
  const [figures, setFigures] = useState<RecipeLineFigures | null>(null)
  const set = (patch: Partial<RecipeLineDraft>) => setDraft((was) => ({ ...was, ...patch }))
  /* What a blank box prints, so its placeholder: the record's customer-facing name, else its name. */
  const followed = catalogCustomerName || name

  const priced = (): RecipeLineDraft => {
    const typed = Number(quoted)
    return {
      ...draft,
      priceOverride: Number.isFinite(typed) && typed !== standard ? Math.round(typed * 100) / 100 : null,
      customerName: customerNameToStore(customerName, followed),
    }
  }

  // The figures follow the rule and the price, a quarter-second after the typing stops.
  useEffect(() => {
    let live = true
    const timer = window.setTimeout(() => {
      void onPreview(priced()).then((next) => { if (live) setFigures(next) })
    }, 250)
    return () => { live = false; window.clearTimeout(timer) }
  }, [draft, quoted])

  return (
    <EditorShell
      open
      onClose={onClose}
      anchor={anchor}
      layer={layer}
      label={name}
      header={<EditorHeader kind={kind} name={name} facts={facts} />}
      footer={(
        <EditorActions
          fact={<FooterTotal label={LINE_TOTAL} amount={figures ? formatRate(Number(figures.amount)) : '—'} />}
          onRemove={() => { onRemove(); onClose() }}
          onDone={() => { onDone(priced()); onClose() }}
        />
      )}
    >
      <div className="flex flex-col gap-3.5">
        {/*
          * The quote line popover's box, one level up: what every quote made
          * from this recipe prints for the line, until somebody renames it
          * there. Blank follows the catalog, and says so in its placeholder.
          */}
        <FieldShell label={CUSTOMER_NAME_LABEL} hint={CUSTOMER_NAME_RECIPE_HINT}>
          <EditorInput
            value={customerName}
            placeholder={followed}
            maxLength={CUSTOMER_NAME_MAX}
            aria-label={CUSTOMER_NAME_LABEL}
            onChange={(event) => setCustomerName(event.target.value)}
          />
        </FieldShell>

        {measureUnit
          ? (
            <QuantityRuleEditor
              base={draft.base}
              rate={draft.rate}
              onBase={(base) => set({ base })}
              onRate={(rate) => set({ rate })}
              tiers={draft.tiers}
              onTiers={(tiers) => set({ tiers })}
              qtyUnit={qtyUnit}
              measureUnit={measureUnit}
              typeName={typeName}
              sample={sample}
              tiersSlot={draft.legacyBands
                ? (
                  <LegacyBands
                    line={{ baseQty: draft.base, qtyPerUnit: draft.rate, bands: draft.legacyBands }}
                    unit={measureUnit}
                    onChange={(legacyBands) => set({ legacyBands })}
                  />
                )
                : undefined}
            />
          )
          : (
            <FieldShell label={QTY_BASE_LABEL}>
              <UnitInput label={QTY_BASE_LABEL} value={draft.base} onChange={(base) => set({ base })} unit={qtyUnit} />
            </FieldShell>
          )}

        <div className="border-t border-line-soft" />

        <div className="grid grid-cols-3 gap-3">
          <FieldShell label={OUR_COST_PER(priceUnit)}>
            <span className={cn(readOnlyCell, '!justify-start gap-1.5 font-normal')}>
              <span className="text-text-faint"><IconLock size={12} /></span>{formatUnitCost(unitCost)}
            </span>
          </FieldShell>
          <FieldShell label={STANDARD_PER(priceUnit)}>
            <span className={cn(readOnlyCell, '!justify-start font-normal')}>{formatRate(standard)}</span>
          </FieldShell>
          <FieldShell label={QUOTED_PER(priceUnit)}>
            <UnitInput label={QUOTED_PER(priceUnit)} value={quoted} onChange={setQuoted} prefix="$" />
          </FieldShell>
        </div>

        <div className="grid grid-cols-[auto_auto_auto_1fr] gap-x-8 rounded-[10px] bg-surface-alt px-4 py-3">
          <span className="flex flex-col text-[11.5px] text-text-muted">
            {measureUnit ? AT_MEASURE(formatNumber(sample), measureUnit) : LINE_TOTAL}
            <b className="tnum text-[20px] font-bold text-text">
              {figures ? formatRate(Number(figures.amount)) : '—'}
            </b>
          </span>
          <span className="flex flex-col text-[11.5px] text-text-muted">
            {MARGIN_TITLE}
            <b className="tnum text-[20px] font-bold text-accent">
              {figures?.marginPct !== null && figures?.marginPct !== undefined ? `${figures.marginPct.toFixed(1)}%` : '—'}
            </b>
          </span>
          <span className="flex flex-col text-[11.5px] text-text-muted">
            {MARKUP}
            <b className="tnum text-[20px] font-bold text-text">
              {figures?.markupPct !== null && figures?.markupPct !== undefined ? markupWords(figures.markupPct) : '—'}
            </b>
          </span>
        </div>

        <div className="border-t border-line-soft" />

        <OptionsBlock
          level="line"
          optional={{ checked: draft.optional, onChange: (optional) => set({ optional }) }}
          showOnProposal={{ checked: draft.showOnProposal, onChange: (showOnProposal) => set({ showOnProposal }) }}
          taxable={{
            checked: draft.taxable ?? catalogTaxable,
            onChange: (taxable) => set({ taxable: taxable === catalogTaxable ? null : taxable }),
          }}
          consumesStock={kind === 'item'
            ? {
              checked: (draft.handling ?? 'consume') === 'consume',
              onChange: (next) => set({ handling: next ? null : 'deploy_return' }),
            }
            : undefined}
        />
      </div>
    </EditorShell>
  )
}
